import microMemoize from 'micro-memoize';
import isEqual from 'react-fast-compare';

const getWindow = () => {
    return globalThis.window;
};
const getDocument = () => {
    const window = getWindow();
    if (window) {
        return window.document;
    }
};
const getHTMLElement = () => {
    const window = getWindow();
    if (window) {
        return globalThis.HTMLElement;
    }
};
const getNavigator = () => {
    const window = getWindow();
    if (window) {
        return window.navigator;
    }
};

function isPromise(value) {
    return Boolean(value && typeof value.then === 'function' && typeof value.catch === 'function');
}

const callbackify = (action, handle) => {
    let result;
    try {
        result = action();
    }
    catch (error) {
        handle(error);
        return;
    }
    if (isPromise(result)) {
        return result.then((data) => {
            handle(undefined, data);
        }).catch((error) => {
            handle(error);
        });
    }
    handle(undefined, result);
    return result;
};

/**
 * remove keys which has undefined value
 * but don’t make a case to use this
 * @param value
 */
const cleanObject = (value) => (Object.keys(value).reduce((result, key) => {
    const _value = value[key];
    if (typeof _value === 'undefined' || _value === null) {
        return result;
    }
    result[key] = _value;
    return result;
}, {}));

/**
 * drops one item from an array
 */
const drop = (array) => {
    const value = [...array];
    value.shift();
    return value;
};

function freeze(record) {
    /* istanbul ignore next */
    if (process.env.NODE_ENV === 'development') {
        return Object.freeze(record);
    }
    // in production no need to use freeze
    /* istanbul ignore next */
    return record;
}

const _getStorage = (type) => {
    const window = getWindow();
    switch (type) {
        // eslint-disable-next-line indent
        case 'local':
            return window === null || window === void 0 ? void 0 : window.localStorage;
        case 'session':
            return window === null || window === void 0 ? void 0 : window.sessionStorage;
    }
};
/**
 * a key for testing setItem
 */
const TEST_KEY_VALUE = '__storage_test__';
const createGetStorage = () => {
    let _testResult;
    return (type) => {
        const storage = _getStorage(type);
        if (!storage) {
            return;
        }
        // skip if it has test result
        if (typeof _testResult === 'undefined') {
            // test setItem
            try {
                storage.setItem(TEST_KEY_VALUE, TEST_KEY_VALUE);
                storage.removeItem(TEST_KEY_VALUE);
                _testResult = true;
            }
            catch (_a) {
                // save test result
                _testResult = false;
            }
        }
        if (_testResult) {
            return storage;
        }
    };
};
const getStorage = createGetStorage();

const isListenable = () => {
    const window = getWindow();
    if (!window) {
        return false;
    }
    const document = getDocument();
    if (!document) {
        return false;
    }
    return Boolean(document.addEventListener);
};

// no need to cover all
/* istanbul ignore next */
/**
 * @deprecated
 */
const isSSR = () => { var _a, _b; return typeof ((_b = (_a = globalThis.window) === null || _a === void 0 ? void 0 : _a.document) === null || _b === void 0 ? void 0 : _b.createElement) === 'undefined'; };

function mayFunctionValue(value, args) {
    if (typeof value === 'function') {
        return value(...args !== null && args !== void 0 ? args : []);
    }
    return value;
}

const memoize = (options) => (function_) => (microMemoize(function_, options));
const deepMemoize = (options) => (function_) => (microMemoize(function_, Object.assign({ isEqual }, options)));

const nanNumber = (value, defaultValue = 0) => {
    const mayNumber = Number(value);
    if (Number.isNaN(mayNumber)) {
        return defaultValue;
    }
    return mayNumber;
};

const promisify = (function_) => (...args) => {
    return new Promise((resolve, reject) => {
        function_(...args, (error, result) => (error ? reject(error) : resolve(result)));
    });
};

const trimUrlRegx = /^[\s/]+|[\s/]+$/ugi;
const protocolRegx = /^(?:https?|ftp):\/\//u;
const hostRegx = /^[_a-z-]+(?:\.[_a-z-]+){1,2}$/u;
// ^? 로 안하는 이유는 jest 테스트 컴파일중 신택스 오류가 있어서
const firstQueryRegx = /^[?]/u;
const trimQueryRegx = /^[?&]/u;
/**
 * "/ /s" 를 각각 trim 하고 "/"로 연결 합니다
 * @param urls 주소 배열
 */
const resolveUrl = (...urls) => {
    return urls.map((url) => {
        return url.replace(trimUrlRegx, '');
    }).join('/');
};
/**
 * url 를 분해 합니다
 * @param url 주소 문자열
 */
const chunkUrl = (url) => {
    const leftUrl = url.replace(protocolRegx, '');
    const [protocol] = url.split(leftUrl);
    const [maybeHost, ...chunkedUrl] = leftUrl.split('/');
    const hasHost = hostRegx.test(maybeHost);
    const result = {
        chunkedUrl: [],
    };
    if (protocol) {
        result.protocol = protocol;
    }
    if (hasHost) {
        result.host = maybeHost;
        result.chunkedUrl = chunkedUrl;
    }
    else {
        result.chunkedUrl = [maybeHost, ...chunkedUrl];
    }
    return result;
};
/**
 * join each string with "&" queries and prepend "?"
 * @param queries
 */
const joinQuery = (...queries) => {
    const [first, ...restQueries] = queries;
    const _first = first.replace(firstQueryRegx, '');
    return `?${[_first, ...restQueries].join('&')}`;
};
/**
 * key value 패어에서 one query string 리턴
 * todo value array 와 object 도 지원 가능 하게
 * @param key
 * @param value
 */
const queryFromEntire = (key, value) => {
    const _key = key.replace(trimQueryRegx, '');
    if (process.env.NODE_ENV === 'development' && key.length !== _key.length) {
        console.warn('key start with ? or &');
    }
    return `${_key}=${encodeURIComponent(value)}`;
};
const resolveQuery = (record) => {
    const list = Object.entries(record).map(([key, value]) => queryFromEntire(key, value));
    return joinQuery(...list);
};

const setTimeoutPromise = (time) => {
    return new Promise((resolve) => {
        setTimeout(resolve, time);
    });
};

const toArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === 'undefined') {
        return [];
    }
    if (value === null) {
        return [];
    }
    return [value];
};

export { callbackify, chunkUrl, cleanObject, createGetStorage, deepMemoize, drop, freeze, getDocument, getHTMLElement, getNavigator, getStorage, getWindow, isListenable, isPromise, isSSR, joinQuery, mayFunctionValue, memoize, nanNumber, promisify, queryFromEntire, resolveQuery, resolveUrl, setTimeoutPromise, toArray };
